// İSG Portal — EmptyState (S34-2 design review §2). Carbon'un empty-states desenini izler: sol
// hizalı blok, olumlu bir başlık, tek satır rehberlik, en fazla bir aksiyon.
//
// İKİ variant:
// - `table` (varsayılan) — bir listenin/tablonun `TableMessageRow`ı İÇİNDE render edilir: 32px
//   ikon (`text-muted`, strokeWidth 1.5) + `text-base font-semibold` başlık + `text-sm
//   text-muted` gövde + opsiyonel `Button variant="ghost" size="sm"` aksiyon. HER listede İKİ
//   ayrı çağrı beklenir: "veri yok" (oluşturma aksiyonu, yalnız yetkiliyse) ile "sonuç yok"
//   (filtrelerle eşleşmedi — "Filtreleri temizle" aksiyonu, `role="status"` ile duyurulur).
// - `panel` — metin-yalnız tek satır (ikon YOK): dashboard panelleri, detay alt-panelleri, modal
//   gövdeleri (review §2 "Carbon: birden çok boş durum aynı anda görünebilirse ikon YERİNE metin
//   kullan"). `icon`/`description` bu variant'ta YOK SAYILIR — `title` tek satırı taşır.
//
// `role` (opsiyonel `"status"`) — bir filtre değişikliği SONRASI görünen "sonuç yok" durumu için:
// ekran okuyucu kullanıcısı navigasyon OLMADAN değişen içeriği duyurmalı (depo a11y kuralı,
// "Async sonuçlar bir canlı bölge gerektirir").

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface EmptyStateProps {
  /** @default 'table' */
  variant?: 'table' | 'panel';
  /** `table` variant'ında 32px ikon — `panel`de YOK SAYILIR (dosya başlığı). */
  icon?: LucideIcon;
  /** Ana satır — `table`da kalın başlık, `panel`de tek satırın kendisi. */
  title: ReactNode;
  /**
   * `table` variant'ında başlığın öğesi (additive, S37-6) — varsayılan `p` (mevcut çağıranlar
   * DEĞİŞMEZ). Tam sayfa durum bildirimlerinde (kapı/erişim) gerçek bir başlık gerekir.
   */
  titleAs?: 'p' | 'h1' | 'h2' | 'h3';
  /** İkinci satır (rehberlik) — yalnız `table` variant'ında render edilir. */
  description?: ReactNode;
  /** En fazla bir aksiyon (ör. "Yeni …" ya da "Filtreleri temizle"). */
  action?: ReactNode;
  /** "Sonuç yok" (filtre sonrası) durumunda `"status"` — canlı bölge (dosya başlığı). */
  role?: 'status';
  className?: string;
}

export function EmptyState({
  variant = 'table',
  icon: Icon,
  title,
  titleAs: TitleTag = 'p',
  description,
  action,
  role,
  className,
}: EmptyStateProps) {
  if (variant === 'panel') {
    return (
      <div role={role} className={cn('flex flex-col items-start gap-2 text-left', className)}>
        <p className="text-sm text-muted">{title}</p>
        {/* `description` (S34-4 — additive) — panel variant'ta eskiden YOK SAYILIYORDU (dosya
            başlığı); dashboard panellerinin (ör. "Aksiyon gerekli") çift satırlı boş durum
            metni (başlık + rehberlik) İÇİN eklendi. Bugüne kadar hiçbir çağıran `panel` +
            `description`'ı BİRLİKTE vermiyordu (doğrulandı), bu yüzden geriye dönük DAVRANIŞ
            DEĞİŞİKLİĞİ yok — yalnız yeni bir kombinasyon artık render edilir. */}
        {description ? <p className="text-sm text-muted">{description}</p> : null}
        {action ? <div>{action}</div> : null}
      </div>
    );
  }
  return (
    <div
      role={role}
      className={cn(
        'mx-auto flex max-w-[28rem] flex-col items-start gap-2 py-12 text-left',
        className,
      )}
    >
      {Icon ? <Icon className="h-8 w-8 text-muted" aria-hidden="true" /> : null}
      <TitleTag className="text-base font-semibold text-fg">{title}</TitleTag>
      {description ? <p className="text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
